import { Elements, useElements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	type ComponentProps,
	type PropsWithChildren,
} from 'react';
import { useWindowSize } from 'react-use';

import { usePricesState, usePurchaseParamsState } from '@/hooks/store';
import useIsCalmSleepApp from '@/hooks/useIsCalmSleepApp';
import { getPrice } from '@/store/purchaseParams/types';
import { getClassicStripe } from '@/utils/getStripe';
import { STRIPE_ZERO_DECIMAL_CURRENCIES } from '@/utils/prices/constants';

import {
	desktopSleepAiStripeAppearanceOptions,
	desktopStripeAppearanceOptions,
	sleepAiStripeAppearanceOptions,
	stripeAppearanceOptions,
} from './utils';

export const StripeElementProviderContext = createContext<{ isStripeNextEnabled: boolean }>({
	isStripeNextEnabled: false,
});

// This is a temporary solution to update the  Stripe Elements' options if these values change
// during the lifetime of the component. This is necessary because the `Elements` component does not
// update its options when they change.
// Eventually, we should ensure purchase params are only set on the server so that these values don't update.
const ElementsUpdater: React.FC<
	PropsWithChildren<{
		amount?: number;
		mode: StripeElementsOptions['mode'];
		currency: string;
	}>
> = ({ children, amount, mode, currency }) => {
	const elements = useElements();

	useEffect(() => {
		const update = async () => {
			// `amount` belongs only to the amount-bearing modes (`payment`/`subscription`); omit it
			// for `mode: 'setup'` so the update stays consistent with the discriminated-union options
			// passed to `<Elements>` (a SetupIntent carries no amount).
			await elements?.update?.(mode === 'setup' ? { mode, currency } : { mode, amount, currency });
		};
		update().catch(() => {});
	}, [amount, elements, mode, currency]);

	return <>{children}</>;
};

const StripeElementsProvider: React.FC<
	PropsWithChildren<{
		isStripeNext?: boolean;
		amount?: number;
		currency?: string;
		mode?: StripeElementsOptions['mode'];
		useAiSleepStoriesTheme?: boolean;
		stripe?: ComponentProps<typeof Elements>['stripe'];
	}>
> = ({
	children,
	isStripeNext = false,
	amount: amountOverride,
	currency: currencyOverride,
	mode: modeOverride,
	useAiSleepStoriesTheme = false,
	stripe = getClassicStripe(),
}) => {
	const prices = usePricesState();
	const { plan, purchaseType } = usePurchaseParamsState();
	const { width: winWidth } = useWindowSize();
	const isStripeNextEnabled = Boolean(isStripeNext);
	const isCalmSleepApp = useIsCalmSleepApp();

	let paymentMethodConfiguration: string | undefined;
	if (!isCalmSleepApp) {
		paymentMethodConfiguration = process.env.NEXT_PUBLIC_STRIPE_PAYMENT_METHOD_CONFIGURATION_ID;
	}

	const currency = (currencyOverride ?? prices?.current?.currency ?? 'usd').toLowerCase();
	let amount = amountOverride ?? getPrice(plan, prices.current);

	if (STRIPE_ZERO_DECIMAL_CURRENCIES.has(currency)) {
		amount = Math.round(amount / 100);
	}

	const isFreeTrial = purchaseType?.type === 'freetrial';
	const mode = modeOverride ?? (isFreeTrial ? 'setup' : 'payment');

	const isDesktop = winWidth > 481;

	const stripeElementsOptions = (() => {
		if (useAiSleepStoriesTheme) {
			return isDesktop ? desktopSleepAiStripeAppearanceOptions : sleepAiStripeAppearanceOptions;
		}
		return isDesktop ? desktopStripeAppearanceOptions : stripeAppearanceOptions;
	})();

	// stripe-js 9's `StripeElementsOptions` is a discriminated union on `mode`; construct each
	// branch explicitly so TS narrows it (no cast). `amount` belongs only to the amount-bearing
	// modes (`payment`/`subscription`) — the free-trial and card-update paths use `mode: 'setup'`
	// (a SetupIntent, no amount). Mirrors the Sisu/PaymentScreen v9 fix for parity.
	const options: StripeElementsOptions = useMemo(() => {
		if (!isStripeNextEnabled) {
			return {};
		}

		const base = {
			currency,
			appearance: stripeElementsOptions,
			paymentMethodConfiguration,
			paymentMethodCreation: 'manual' as const,
		};

		return mode === 'setup'
			? { ...base, mode: 'setup' }
			: { ...base, mode, amount: isFreeTrial ? 0 : amount };
	}, [
		isStripeNextEnabled,
		isFreeTrial,
		amount,
		currency,
		stripeElementsOptions,
		mode,
		paymentMethodConfiguration,
	]);

	return (
		<StripeElementProviderContext.Provider value={{ isStripeNextEnabled }}>
			<Elements stripe={stripe} options={options}>
				<ElementsUpdater mode={mode} amount={isFreeTrial ? 0 : amount} currency={currency}>
					{children}
				</ElementsUpdater>
			</Elements>
		</StripeElementProviderContext.Provider>
	);
};

export default StripeElementsProvider;

export const useStripeElements = () => {
	return useContext(StripeElementProviderContext);
};
